/**
 * Whether a Shopaholic seller has a physical shop, sells online, or both.
 *
 * ## The field already exists
 *
 * `businessType: 'Online' | 'Physical' | 'Both'` has been on the profile since
 * the business forms were built, and the public profile card already renders it.
 * What never happened is that any retail seller was asked to set it, so for the
 * entire life of the vertical it has been blank on every Shopaholic profile.
 *
 * This module is the layer that was missing: a single place that decides what
 * "unset" means and refuses to guess.
 *
 * ## Why it refuses to guess
 *
 * The tempting default is "Online", because the field is blank most often and it
 * is the least committal answer. It is also the wrong answer for a shop with a
 * shopfront, and it has consequences beyond a label:
 *
 *  - `BusinessMap` drops a profile from the map entirely when `businessType` is
 *    `'Online'`, so a physical shop that was defaulted to online silently
 *    disappears from local discovery.
 *  - An online-only seller is never asked for a street address, so they should
 *    never have one published.
 *
 * Silently choosing either answer damages a real trade. So an unset seller is
 * simply unset, and the question gets asked.
 */

export type RetailSalesMode = 'physical' | 'online' | 'both';

const VALID: readonly RetailSalesMode[] = ['physical', 'online', 'both'] as const;

export function parseRetailSalesMode(value: unknown): RetailSalesMode | null {
  if (value === 'physical' || value === 'online' || value === 'both') {
    return value;
  }
  return null;
}

/**
 * Read the answer from a profile, in its canonical form.
 *
 * Reads `retailSalesMode` first, falling back to the pre-existing
 * `businessType` field so a seller who set it through a business form is not
 * asked again. Returns null when neither has been answered - which is a state
 * the UI must handle, not an error.
 */
export function resolveRetailSalesMode(profile: unknown): RetailSalesMode | null {
  if (!profile || typeof profile !== 'object') return null;
  const bag = profile as Record<string, unknown>;

  const explicit = parseRetailSalesMode(bag.retailSalesMode);
  if (explicit) return explicit;

  // Legacy field. Capitalised values, because that is what the business forms
  // have always written.
  const legacy = bag.businessType;
  if (typeof legacy === 'string') {
    const lowered = legacy.trim().toLowerCase();
    if (lowered === 'physical') return 'physical';
    if (lowered === 'online') return 'online';
    if (lowered === 'both') return 'both';
  }

  return null;
}

/** A seller who sells from a premises a customer can visit. */
export function sellsFromPhysicalPremises(mode: RetailSalesMode | null): boolean {
  return mode === 'physical' || mode === 'both';
}

/** A seller a customer can buy from without travelling. */
export function sellsOnline(mode: RetailSalesMode | null): boolean {
  return mode === 'online' || mode === 'both';
}

/**
 * Should this seller be asked for a street address?
 *
 * Only a physical or combined shop. Asking an online-only seller for the address
 * of a home or a depot is how residential addresses end up published.
 */
export function requiresStreetAddress(mode: RetailSalesMode | null): boolean {
  return sellsFromPhysicalPremises(mode);
}

export const RETAIL_SALES_MODE_OPTIONS: ReadonlyArray<{
  value: RetailSalesMode;
  title: string;
  blurb: string;
}> = [
  {
    value: 'physical',
    title: 'I have a shop customers can visit',
    blurb: 'A storefront, market stall or office people come to. You can add an online shop as well.',
  },
  {
    value: 'online',
    title: 'I sell online only',
    blurb: 'You ship from a depot or work from home. No street address is collected or published.',
  },
  {
    value: 'both',
    title: 'I have a shop and sell online',
    blurb: 'Customers can visit you, or order online and have it delivered or collected.',
  },
];